<script setup>
/**
 * The builder's center canvas: a dotted backdrop with a real-size rendering
 * of the campaign message as the chat widget shows it — branded header,
 * image, headline, body, CTA buttons and the launcher bubble. The
 * desktop/mobile toggle constrains the widget to the width each device
 * actually gets.
 *
 * Every content element is click-to-edit: clicking emits `edit` with a
 * target ({ type, index }) and the page opens the matching popover anchored
 * to the element (exposed through `getAnchorEl`). The hovered/active
 * element wears a dashed outline.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getContrastingTextColor } from '@chatwoot/utils';
import { resolvePresetGradient } from 'shared/helpers/campaignImagePresets';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  CTA_TYPES,
  DEFAULT_THEME_COLOR,
  MAX_CTAS,
  isValidThemeColor,
} from 'dashboard/components-next/Campaigns/helpers/campaignContent';
import { themeGradient } from 'dashboard/components-next/Campaigns/helpers/campaignBuilder';

const props = defineProps({
  /** Normalized content: { headline, body, imageUrl, imageAlt, themeColor, ctas }. */
  content: {
    type: Object,
    required: true,
  },
  /** The element whose popover is open: { type, index } or null. */
  activeTarget: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['edit', 'addCta']);

const device = defineModel('device', { type: String, default: 'desktop' });

const { t } = useI18n();

const DEVICES = ['desktop', 'mobile'];

const imageFailed = ref(false);
watch(
  () => props.content.imageUrl,
  () => {
    imageFailed.value = false;
  }
);

const presetGradient = computed(() =>
  resolvePresetGradient(props.content.imageUrl)
);

const showImage = computed(() =>
  Boolean(
    props.content.imageUrl && (presetGradient.value || !imageFailed.value)
  )
);

// The custom-color field feeds this on every keystroke, so a half-typed or
// malformed hex reaches here. `themeGradient` would turn it into NaN colour
// stops and the browser would drop the whole gradient, blanking the header and
// the launcher bubble — fall back until the value is a complete hex.
const theme = computed(() =>
  isValidThemeColor(props.content.themeColor)
    ? props.content.themeColor
    : DEFAULT_THEME_COLOR
);
const headerStyle = computed(() => ({
  backgroundImage: themeGradient(theme.value),
}));
const headerTextColor = computed(() => getContrastingTextColor(theme.value));

const canAddCta = computed(() => props.content.ctas.length < MAX_CTAS);

const ctaClasses = (cta, index) => {
  if (cta.type === CTA_TYPES.DISMISS) return 'bg-transparent text-n-slate-11';
  return index === 0 ? '' : 'border bg-transparent';
};

const ctaStyle = (cta, index) => {
  if (cta.type === CTA_TYPES.DISMISS) return {};
  if (index === 0) {
    return {
      backgroundColor: theme.value,
      color: getContrastingTextColor(theme.value),
    };
  }
  return { color: theme.value, borderColor: theme.value };
};

/**
 * Template-ref registry so the page can anchor popovers to elements. The
 * image slot swaps between two elements (placeholder <-> image) and Vue's
 * unmount ref callback for the old element can run after the new element
 * registered — only clear a slot when its stored element really left the
 * document, so the registry always ends on the mounted one.
 */
const anchorEls = {};
const setAnchor = key => el => {
  if (el) {
    anchorEls[key] = el;
  } else if (anchorEls[key] && !anchorEls[key].isConnected) {
    anchorEls[key] = null;
  }
};
const anchorKeyFor = target =>
  target.type === 'cta' ? `cta-${target.index}` : target.type;
const getAnchorEl = target => anchorEls[anchorKeyFor(target)] ?? null;
defineExpose({ getAnchorEl });

const isActive = key =>
  Boolean(props.activeTarget) && anchorKeyFor(props.activeTarget) === key;

const editableClasses = key => [
  'outline-dashed outline-1 outline-offset-2 transition-colors',
  isActive(key)
    ? 'outline-n-violet-9'
    : 'outline-transparent hover:outline-n-violet-8',
];

// Each editable element opens the click-to-edit popover. The dashed outline
// conveys the open state visually only, so announce the relationship and the
// state to assistive tech too.
const editableAria = key => ({
  'aria-haspopup': 'dialog',
  'aria-expanded': isActive(key) ? 'true' : 'false',
});

const emitEdit = (type, index) => emit('edit', { type, index });

const deviceChipClasses = active => [
  'inline-flex items-center gap-1.5 h-7 px-3 rounded-full font-medium text-label-small transition-colors',
  active
    ? 'bg-n-solid-active text-n-slate-12 shadow-sm outline outline-1 outline-n-container'
    : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
];
</script>

<template>
  <div class="relative flex flex-col flex-1 min-w-0 min-h-0 bg-n-solid-1">
    <div
      class="absolute inset-0 bg-[radial-gradient(circle,_rgba(0,0,0,0.08)_1px,_transparent_1px)] dark:bg-[radial-gradient(circle,_rgba(255,255,255,0.08)_1px,_transparent_1px)] [background-size:18px_18px]"
      aria-hidden="true"
    />

    <div
      role="group"
      :aria-label="t('CAMPAIGN.BUILDER.PREVIEW.DEVICE_GROUP_LABEL')"
      class="relative z-10 flex items-center self-center gap-1.5 mt-4"
    >
      <button
        v-for="option in DEVICES"
        :key="option"
        type="button"
        :class="deviceChipClasses(device === option)"
        :aria-pressed="device === option"
        @click="device = option"
      >
        <Icon
          :icon="
            option === 'desktop' ? 'i-lucide-monitor' : 'i-lucide-smartphone'
          "
          class="size-3.5"
        />
        {{ t(`CAMPAIGN.BUILDER.PREVIEW.${option.toUpperCase()}`) }}
      </button>
    </div>

    <div class="relative z-10 flex-1 min-h-0 p-8 overflow-y-auto">
      <div
        class="flex flex-col items-end justify-end min-h-full gap-3 mx-auto transition-all duration-300"
        :class="device === 'mobile' ? 'max-w-[320px]' : 'max-w-[400px]'"
      >
        <div
          class="flex flex-col w-full overflow-hidden shadow-lg rounded-2xl rounded-br-md bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-container"
        >
          <!-- Branded widget header, driven by the theme color. -->
          <div
            class="flex items-center gap-2.5 px-4 py-3"
            :style="headerStyle"
            aria-hidden="true"
          >
            <span
              class="flex items-center justify-center rounded-full size-8 bg-white/20"
            >
              <Icon
                icon="i-lucide-message-circle"
                class="size-4"
                :style="{ color: headerTextColor }"
              />
            </span>
            <span
              class="text-sm font-semibold"
              :style="{ color: headerTextColor }"
            >
              {{ t('CAMPAIGN.BUILDER.PREVIEW.HEADER_TITLE') }}
            </span>
          </div>

          <button
            v-if="showImage"
            :ref="setAnchor('image')"
            type="button"
            class="w-full"
            :class="editableClasses('image')"
            :aria-label="t('CAMPAIGN.BUILDER.PREVIEW.EDIT_IMAGE')"
            v-bind="editableAria('image')"
            @click="emitEdit('image')"
          >
            <div
              v-if="presetGradient"
              class="w-full h-32"
              :style="{ backgroundImage: presetGradient }"
              role="img"
              :aria-label="content.imageAlt"
            />
            <img
              v-else
              :src="content.imageUrl"
              :alt="content.imageAlt"
              class="object-cover w-full h-40 max-w-full"
              @error="imageFailed = true"
            />
          </button>

          <div class="flex flex-col gap-2 p-4">
            <button
              v-if="!showImage"
              :ref="setAnchor('image')"
              type="button"
              class="flex items-center justify-center gap-1.5 h-12 rounded-lg border border-dashed border-n-strong text-n-slate-10 text-label-small font-medium hover:text-n-slate-11 hover:border-n-slate-8 transition-colors"
              :class="editableClasses('image')"
              :aria-label="t('CAMPAIGN.BUILDER.PREVIEW.ADD_IMAGE')"
              v-bind="editableAria('image')"
              @click="emitEdit('image')"
            >
              <Icon icon="i-lucide-image-plus" class="size-4" />
              {{ t('CAMPAIGN.BUILDER.PREVIEW.ADD_IMAGE') }}
            </button>

            <button
              :ref="setAnchor('headline')"
              type="button"
              class="text-base font-semibold text-left rounded-md text-n-slate-12"
              :class="[
                editableClasses('headline'),
                { 'text-n-slate-10': !content.headline },
              ]"
              :aria-label="t('CAMPAIGN.BUILDER.PREVIEW.EDIT_HEADLINE')"
              v-bind="editableAria('headline')"
              @click="emitEdit('headline')"
            >
              {{
                content.headline ||
                t('CAMPAIGN.BUILDER.PREVIEW.HEADLINE_PLACEHOLDER')
              }}
            </button>

            <button
              :ref="setAnchor('body')"
              type="button"
              class="text-left rounded-md"
              :class="editableClasses('body')"
              :aria-label="t('CAMPAIGN.BUILDER.PREVIEW.EDIT_BODY')"
              v-bind="editableAria('body')"
              @click="emitEdit('body')"
            >
              <!-- Plain text, matching the widget's CampaignCard exactly —
                   rendering markdown here would promise formatting the
                   visitor never gets. -->
              <p v-if="content.body" class="mb-0 text-sm text-n-slate-11">
                {{ content.body }}
              </p>
              <p v-else class="mb-0 text-sm text-n-slate-10">
                {{ t('CAMPAIGN.BUILDER.PREVIEW.BODY_PLACEHOLDER') }}
              </p>
            </button>

            <div v-if="content.ctas.length" class="flex flex-col gap-2 mt-1">
              <button
                v-for="(cta, index) in content.ctas"
                :key="index"
                :ref="setAnchor(`cta-${index}`)"
                type="button"
                class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-lg"
                :class="[
                  ctaClasses(cta, index),
                  editableClasses(`cta-${index}`),
                ]"
                :style="ctaStyle(cta, index)"
                :aria-label="t('CAMPAIGN.BUILDER.PREVIEW.EDIT_CTA')"
                v-bind="editableAria(`cta-${index}`)"
                @click="emitEdit('cta', index)"
              >
                {{ cta.label || t('CAMPAIGN.BUILDER.PREVIEW.CTA_PLACEHOLDER') }}
              </button>
            </div>
          </div>
        </div>

        <!-- The add-button affordance lives under the card, out of frame. -->
        <button
          type="button"
          class="inline-flex items-center self-stretch justify-center gap-1.5 h-9 rounded-lg border border-dashed border-n-strong text-label-small font-medium transition-colors"
          :class="
            canAddCta
              ? 'text-n-slate-11 hover:text-n-slate-12 hover:border-n-slate-8'
              : 'text-n-slate-8 cursor-not-allowed'
          "
          :disabled="!canAddCta"
          @click="emit('addCta')"
        >
          <Icon icon="i-lucide-plus" class="size-3.5" />
          {{
            canAddCta
              ? t('CAMPAIGN.BUILDER.PREVIEW.ADD_CTA')
              : t('CAMPAIGN.BUILDER.PREVIEW.CTA_LIMIT', { max: MAX_CTAS })
          }}
        </button>

        <!-- The launcher bubble the widget renders beneath campaign messages. -->
        <div
          class="flex items-center justify-center rounded-full shadow-lg size-12"
          :style="headerStyle"
          aria-hidden="true"
        >
          <Icon
            icon="i-lucide-message-circle"
            class="size-6"
            :style="{ color: headerTextColor }"
          />
        </div>
      </div>
    </div>
  </div>
</template>
